import type { ReactNode } from "react";
import { useState } from "react";

export type Column<T> = {
  key: string;
  label: string;
  render: (row: T) => ReactNode;
  /** Omit for a column that shouldn't be clickable to sort (e.g. an actions column). */
  sortValue?: (row: T) => string | number;
};

export function sortRows<T>(rows: T[], column: Column<T> | undefined, dir: 1 | -1): T[] {
  if (!column?.sortValue) return rows;
  const sortValue = column.sortValue;
  return [...rows].sort((a, b) => {
    const av = sortValue(a);
    const bv = sortValue(b);
    if (av < bv) return -1 * dir;
    if (av > bv) return 1 * dir;
    return 0;
  });
}

/** Desktop admin table: sortable headers, row hover. Mobile card lists are untouched by this component. */
export function DataTable<T>({
  columns,
  rows,
  rowKey,
  emptyMessage,
  isPending,
}: {
  columns: Column<T>[];
  rows: T[];
  rowKey: (row: T) => string;
  emptyMessage: string;
  isPending?: boolean;
}) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [dir, setDir] = useState<1 | -1>(1);
  const activeColumn = columns.find((c) => c.key === sortKey);
  const sorted = sortRows(rows, activeColumn, dir);

  function toggleSort(column: Column<T>) {
    if (!column.sortValue) return;
    if (sortKey === column.key) setDir(dir === 1 ? -1 : 1);
    else {
      setSortKey(column.key);
      setDir(1);
    }
  }

  return (
    <div className="overflow-x-auto rounded-xl border border-stone-200 bg-white">
      <table className="w-full text-left text-sm">
        <thead>
          <tr className="border-b border-stone-200 bg-stone-50">
            {columns.map((column) => (
              <th key={column.key} className="px-4 py-3 font-medium text-stone-500">
                {column.sortValue ? (
                  <button
                    type="button"
                    className="flex items-center gap-1 hover:text-stone-900"
                    onClick={() => toggleSort(column)}
                  >
                    {column.label}
                    {sortKey === column.key ? <span aria-hidden="true">{dir === 1 ? "▲" : "▼"}</span> : null}
                  </button>
                ) : (
                  column.label
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {isPending ? (
            <tr>
              <td colSpan={columns.length} className="px-4 py-8 text-center text-stone-500">
                불러오는 중…
              </td>
            </tr>
          ) : (
            <>
              {sorted.map((row) => (
                <tr key={rowKey(row)} className="border-b border-stone-100 last:border-0 hover:bg-stone-50">
                  {columns.map((column) => (
                    <td key={column.key} className="px-4 py-3">
                      {column.render(row)}
                    </td>
                  ))}
                </tr>
              ))}
              {rows.length === 0 ? (
                <tr>
                  <td colSpan={columns.length} className="px-4 py-8 text-center text-stone-500">
                    {emptyMessage}
                  </td>
                </tr>
              ) : null}
            </>
          )}
        </tbody>
      </table>
    </div>
  );
}
